/**
 * TRAINING & CERTIFICATION SCREEN
 *
 * Simple flat list of ALL certification courses.
 * Driver Certification must be completed first before equipment courses unlock.
 * No tabs or category filters — just a clean scrollable list.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Platform, RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { MOCK_COURSES, MOCK_DRIVER } from '../../data/mockData';
import { RootState } from '../../store';

type CertStatus = 'CERTIFIED' | 'PENDING_APPROVAL' | 'NOT_STARTED';

function getCertStatus(categorySlug: string, driverCerts: any[]): CertStatus {
  const cert = driverCerts.find((c: any) => c.categorySlug === categorySlug);
  if (!cert) return 'NOT_STARTED';
  // Backend may return CERTIFIED or APPROVED — treat both as certified
  if (cert.status === 'CERTIFIED' || cert.status === 'APPROVED') return 'CERTIFIED';
  if (cert.status === 'PENDING_APPROVAL' || cert.status === 'PENDING') return 'PENDING_APPROVAL';
  return 'NOT_STARTED';
}

export function TrainingScreen({ navigation }: any) {
  const [courses, setCourses] = useState<any[]>(MOCK_COURSES);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [driverCerts, setDriverCerts] = useState<any[]>(MOCK_DRIVER.certifications);

  const reduxCerts = useSelector((s: RootState) => s.auth.certifications);

  const loadData = useCallback(async () => {
    const [coursesRes, certsRes] = await Promise.all([
      api.get('/training').catch(() => ({ data: null })),
      api.get('/drivers/me/certifications').catch(() =>
        // Fallback to legacy endpoint
        api.get('/driver/certifications').catch(() => ({ data: null }))
      ),
    ]);
    // Primary: use backend courses; merge with MOCK_COURSES for quiz content
    const apiCourses: any[] = coursesRes.data || [];
    const mockMap = new Map(MOCK_COURSES.map((c: any) => [c.categorySlug, c]));
    const apiMap = new Map(apiCourses.map((c: any) => [c.categorySlug, c]));

    // Start from MOCK_COURSES as base (has quiz questions), overlay with API data
    const merged = MOCK_COURSES.map(mock => {
      const apiCourse = apiMap.get(mock.categorySlug);
      if (apiCourse) {
        return {
          ...mock,
          id: apiCourse.id || mock.id,
          status: apiCourse.status || mock.status,
          // Keep mock quiz content — backend courses may not have quiz questions yet
          quizQuestions: mock.quizQuestions,
          textContent: mock.textContent,
          videoUrl: apiCourse.videoUrl || mock.videoUrl,
          _count: mock._count,
          _sortOrder: mock._sortOrder,
        };
      }
      return mock;
    });
    // Add any API-only courses not in mocks (e.g. auto-generated from new job categories)
    for (const apiCourse of apiCourses) {
      if (!mockMap.has(apiCourse.categorySlug)) {
        merged.push({ ...apiCourse, _sortOrder: 50 });
      }
    }
    setCourses(merged);

    // Use backend certifications as primary source
    const backendCerts = certsRes?.data;
    if (Array.isArray(backendCerts) && backendCerts.length > 0) {
      setDriverCerts(backendCerts);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await new Promise(r => setTimeout(r, 600));
    setRefreshing(false);
  };

  // Check if driver cert is completed
  const driverCertPassed = useMemo(() => {
    const driverCertStatus = getCertStatus('driver-certification', driverCerts);
    return driverCertStatus === 'CERTIFIED'
      || driverCertStatus === 'PENDING_APPROVAL'
      || reduxCerts.includes('driver-certification');
  }, [driverCerts, reduxCerts]);

  // Resolve effective status per course (includes Redux auto-approve)
  const courseStatuses = useMemo(() => {
    const map = new Map<string, CertStatus>();
    courses.forEach(c => {
      const apiStatus = getCertStatus(c.categorySlug, driverCerts);
      map.set(c.id, reduxCerts.includes(c.categorySlug) ? 'CERTIFIED' : apiStatus);
    });
    return map;
  }, [courses, driverCerts, reduxCerts]);

  // Certification summary
  const { certifiedCount, totalCount } = useMemo(() => {
    const uniqueCategories = [...new Set(courses.map((c: any) => c.categorySlug))];
    const certified = uniqueCategories.filter(slug =>
      getCertStatus(slug, driverCerts) === 'CERTIFIED' || reduxCerts.includes(slug)
    ).length;
    return { certifiedCount: certified, totalCount: uniqueCategories.length };
  }, [courses, driverCerts, reduxCerts]);

  // Sort by _sortOrder (driver cert first, then Peloton equipment, then priority categories)
  const sortedCourses = useMemo(() => {
    return [...courses].sort((a, b) => {
      const orderA = a._sortOrder ?? 99;
      const orderB = b._sortOrder ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      return a.title.localeCompare(b.title);
    });
  }, [courses]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Training</Text>
          <Text style={styles.subtitle}>Complete courses to unlock job categories</Text>
        </View>
        <View style={styles.courseCount}>
          <Text style={styles.courseCountText}>{courses.length}</Text>
        </View>
      </View>

      {/* Tap to refresh (web) */}
      {Platform.OS === 'web' && !refreshing && (
        <TouchableOpacity style={styles.refreshRow} onPress={onRefresh} activeOpacity={0.7}>
          <Ionicons name="refresh-outline" size={14} color={RD.textFaint} />
          <Text style={styles.refreshText}>Tap to refresh</Text>
        </TouchableOpacity>
      )}
      {refreshing && (
        <View style={styles.refreshRow}>
          <ActivityIndicator size="small" color={RD.ink} />
          <Text style={styles.refreshText}>Refreshing...</Text>
        </View>
      )}

      {/* Certification summary */}
      <View style={styles.summaryBar}>
        <Ionicons name="shield-checkmark" size={16} color={RD.money} />
        <Text style={styles.summaryText}>
          {certifiedCount} of {totalCount} certifications complete
        </Text>
      </View>

      {/* Driver cert required banner (maroon-soft, v5 style) */}
      {!driverCertPassed && (
        <TouchableOpacity
          style={styles.prereqBanner}
          onPress={() => navigation.navigate('Quiz', { courseId: 'course-driver-cert', title: 'General Driver Certification' })}
          activeOpacity={0.8}
        >
          <Ionicons name="shield-checkmark" size={20} color={RD.maroon} />
          <View style={styles.prereqTextWrap}>
            <Text style={styles.prereqTitle}>Driver certification required</Text>
            <Text style={styles.prereqSub}>Finish the general course before accepting jobs.</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={RD.maroon} />
        </TouchableOpacity>
      )}

      {loading ? (
        <ActivityIndicator color={RD.ink} style={{ marginTop: 40 }} />
      ) : (
        <FlatList
          data={sortedCourses}
          keyExtractor={i => i.id}
          contentContainerStyle={styles.list}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={RD.ink} />}
          renderItem={({ item }) => {
            const status = courseStatuses.get(item.id) || 'NOT_STARTED';
            const isDriverCert = item.categorySlug === 'driver-certification';
            const isPeloton = item.categorySlug?.startsWith('peloton-') || item.categorySlug === 'fitness-equipment';
            const isLocked = !isDriverCert && !driverCertPassed;
            const isComplete = status === 'CERTIFIED' && !isLocked;
            const isPending = status === 'PENDING_APPROVAL' && !isLocked;

            // v5: status sub-label below the title
            const statusLabel = isLocked
              ? 'Locked'
              : isComplete
                ? 'Completed'
                : isPending
                  ? 'Pending approval'
                  : 'Not started';
            const statusColor = isLocked
              ? RD.textFaint
              : isComplete
                ? RD.money
                : isPending
                  ? RD.amber
                  : RD.g40;

            return (
              <TouchableOpacity
                style={[styles.card, isLocked && styles.cardLocked]}
                onPress={() => {
                  if (isLocked) return;
                  navigation.navigate('Quiz', { courseId: item.id, title: item.title, categorySlug: item.categorySlug });
                }}
                activeOpacity={isLocked ? 1 : 0.7}
              >
                {/* v5 row: icon · title+status · chevron */}
                <View style={[
                  styles.cardIconWrap,
                  isLocked && styles.cardIconLocked,
                  isComplete && styles.cardIconDone,
                ]}>
                  {isLocked ? (
                    <Ionicons name="lock-closed" size={20} color={RD.g30} />
                  ) : isComplete ? (
                    <Ionicons name="checkmark-circle" size={20} color={RD.money} />
                  ) : isDriverCert ? (
                    <Ionicons name="shield-checkmark" size={20} color={RD.maroon} />
                  ) : isPeloton ? (
                    <Ionicons name="bicycle-outline" size={20} color={RD.maroon} />
                  ) : (
                    <Ionicons name="school" size={20} color={RD.maroon} />
                  )}
                </View>

                <View style={styles.cardInfo}>
                  <Text style={[styles.cardTitle, isLocked && styles.cardTitleLocked]} numberOfLines={2}>
                    {item.title}
                  </Text>
                  <Text style={[styles.cardStatus, { color: statusColor, fontWeight: (isComplete || isPending) ? '600' : '400' }]}>
                    {statusLabel}
                  </Text>
                </View>

                <Ionicons name="chevron-forward" size={18} color={RD.g30} />
              </TouchableOpacity>
            );
          }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container:        { flex: 1, backgroundColor: RD.bg },

  // Header
  header:           { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: spacing.sm },
  title:            { fontSize: 26, fontWeight: '800', color: RD.text, fontFamily: fonts.heavy },
  subtitle:         { fontSize: 14, color: RD.textMuted, marginTop: 4 },
  courseCount:       { backgroundColor: RD.maroonSoft, borderRadius: radii.full, width: 32, height: 32, justifyContent: 'center', alignItems: 'center' },
  courseCountText:   { color: RD.maroon, fontSize: 14, fontWeight: '700' },

  // Summary bar
  summaryBar:       { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: spacing.lg, marginBottom: spacing.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, backgroundColor: RD.moneySoft, borderRadius: 14 },
  summaryText:      { fontSize: 14, fontWeight: '600', color: RD.text },

  // Prerequisite banner (v5: maroon-soft, shield + title/sub + chevron)
  prereqBanner:     { flexDirection: 'row', alignItems: 'center', gap: 12, marginHorizontal: spacing.lg, marginBottom: spacing.md, paddingVertical: 14, paddingHorizontal: 16, backgroundColor: RD.maroonSoft, borderRadius: 18, borderWidth: 1, borderColor: RD.maroon + '25' },
  prereqTextWrap:   { flex: 1 },
  prereqTitle:      { fontSize: 13.5, fontWeight: '700', color: RD.maroon },
  prereqSub:        { fontSize: 12.5, color: RD.textMuted, marginTop: 2, lineHeight: 17 },

  // Refresh
  refreshRow:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 6, marginHorizontal: spacing.lg },
  refreshText:      { fontSize: 12, color: RD.textFaint, fontWeight: '500' },

  list:             { padding: spacing.base, gap: 12, paddingBottom: Platform.OS === 'ios' ? 110 : 88 },

  // Card (v5: horizontal icon · title+status · chevron)
  card:             { flexDirection: 'row', alignItems: 'center', gap: 13, backgroundColor: RD.surface, borderRadius: 18, padding: 16, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  cardLocked:       { opacity: 0.55 },
  cardIconWrap:     { width: 44, height: 44, borderRadius: 13, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  cardIconLocked:   { backgroundColor: RD.g05 },
  cardIconDone:     { backgroundColor: RD.moneySoft },
  cardInfo:         { flex: 1 },
  cardTitle:        { fontSize: 14.5, fontWeight: '700', color: RD.text },
  cardTitleLocked:  { color: RD.textFaint },
  cardStatus:       { fontSize: 12.5, marginTop: 1 },
});
